import { axiosInstance } from '../clients/axiosClient';
import { API_V1_PREFIX } from '../utils/constants';

export interface BloodTestData {
  id: number;
  file_name: string;
  file_path: string;
  upload_date: string;
  status: 'UPLOADED' | 'COMPLETED';
}

export interface BloodTestResponse {
  member_id: string;
  results: BloodTestData[];
  total_count: number;
  status: string;
}

export interface BloodTestUploadRequest {
  file: File;
  test_date?: string;
  description?: string;
}

export interface BloodTestUpdateRequest {
  file_name?: string;
  test_date?: string;
  description?: string;
}

export interface BloodTestDownloadResponse {
  id: number;
  file_name: string;
  file_path: string;
  upload_date: string;
  presigned_url: string;
  content_type: string;
  status: string;
}

export const getMemberBloodTests = async (
  member_id: string,
  limit: number = 10,
  offset: number = 0
): Promise<BloodTestResponse> => {
  try {
    const params = { limit, offset };
    
    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}blood-test/member/${member_id}`,
      { params }
    );

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const uploadMemberBloodTest = async (
  member_id: string,
  uploadData: BloodTestUploadRequest
): Promise<BloodTestData> => {
  try {
    const formData = new FormData();
    formData.append('file', uploadData.file);
    if (uploadData.test_date) {
      formData.append('test_date', uploadData.test_date);
    }
    if (uploadData.description) {
      formData.append('description', uploadData.description);
    }

    const url = `${API_V1_PREFIX}blood-test/admin/upload/${member_id}`;
    const { data } = await axiosInstance.post(url, formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const updateMemberBloodTest = async (
  member_id: string,
  blood_test_id: number,
  updateData: BloodTestUpdateRequest
): Promise<BloodTestData> => {
  try {
    const url = `${API_V1_PREFIX}blood-test/member/${member_id}/${blood_test_id}`;
    const { data } = await axiosInstance.put(url, updateData);

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const deleteMemberBloodTest = async (
  member_id: string,
  blood_test_id: number
): Promise<{ status: string; message: string }> => {
  try {
    const url = `${API_V1_PREFIX}blood-test/member/${member_id}/${blood_test_id}`;
    const { data } = await axiosInstance.delete(url);

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }
};

export const downloadMemberBloodTest = async (
  member_id: string,
  blood_test_id: number
): Promise<string> => {
  try {
    const url = `${API_V1_PREFIX}blood-test/download/${blood_test_id}?member_id=${member_id}`;
    const { data } = await axiosInstance.get<BloodTestDownloadResponse>(url);

    if (!data.presigned_url) {
      throw new Error('No presigned URL found in response');
    }

    return data.presigned_url;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to get download URL');
  }
};

export const markBloodTestAsComplete = async (
  blood_test_id: number
  ): Promise<BloodTestData> => {
  try {
    const url = `${API_V1_PREFIX}blood-test/marks-complete/${blood_test_id}`
    const { data } = await axiosInstance.patch(url)
    return data
  }
  catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error);
  }

}
export interface BloodUploadTrackerResult {
  id: number;
  member_id: string;
  member_first_name: string;
  member_last_name: string;
  plan_type: string;
  file_name: string;
  file_path: string;
  upload_date: string;
  status: string;
  blood_tests_link: string;
}

export interface BloodUploadTrackerResponse {
  results: BloodUploadTrackerResult[];
  total_count: number;
  status: string;
}

export const getBloodUploadTracker = async (
  limit: number = 10,
  offset: number = 0,
  search: string = '',
  plan_type?: string,
  sort_by_name?: boolean,
  sort_order?: 'asc' | 'desc',
  start_date?: string,
  end_date?: string
): Promise<BloodUploadTrackerResponse> => {
  try {
    const params: any = { limit, offset };
    if (search) {
      params.search = search;
    }
    if (plan_type) {
      params.plan_type = plan_type;
    }
    if (sort_by_name) {
      params.sort_by_name = sort_by_name;
      params.sort_order = sort_order || 'asc';
    }
    if (start_date) {
      params.upload_start_date = start_date;
    }
    if (end_date) {
      params.upload_end_date = end_date;
    }

    const { data } = await axiosInstance.get(
      `${API_V1_PREFIX}blood-test/admin/blood-uploads-tracker`,
      { params }
    );

    return data;
  } catch (error: any) {
    if (error.response && error.response.data && error.response.data.detail) {
      throw new Error(error.response.data.detail);
    }
    throw new Error(error.message || 'Failed to fetch blood upload tracker data');
  }
};
